import Vue from 'vue';
import {
  CONSENT_STUDIO_UPDATED_EVENT,
  hasMarketingConsentGranted
} from '~/hooks/useMarketingConsent';

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2000;
const GPT_SCRIPT_SRC = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';
const CONSENT_RECHECK_RETRIES = 10;
const CONSENT_RECHECK_DELAY_MS = 250;

export default ({ app }) => {
  if (!process.client) return;

  const loadedSlotIds = new Set();
  const definedSlots = new Map();

  let gptScriptPromise = null;
  let servicesEnabled = false;
  let slotOnloadListenerRegistered = false;
  let hasStarted = false;
  let handleConsentEvent = null;

  const getSlotContainers = () => {
    const containers = Array.from(document.querySelectorAll('[data-ad-unit-code]'));

    containers.forEach((element, index) => {
      if (!element.id) {
        element.id = `gpt-ad-slot-${index + 1}`;
      }
    });

    return containers.filter((element) => element.id);
  };

  const loadGptScript = () => {
    if (window.googletag && window.googletag.apiReady) {
      return Promise.resolve();
    }

    if (gptScriptPromise) {
      return gptScriptPromise;
    }

    window.googletag = window.googletag || { cmd: [] };

    gptScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = GPT_SCRIPT_SRC;
      script.async = true;
      script.crossOrigin = 'anonymous';
      script.onload = () => resolve();
      script.onerror = (error) => {
        gptScriptPromise = null;
        reject(error);
      };
      document.head.appendChild(script);
    });

    return gptScriptPromise;
  };

  const registerSlotOnloadListener = (pubads) => {
    if (slotOnloadListenerRegistered) return;

    pubads.addEventListener('slotOnload', (event) => {
      const id = event.slot.getSlotElementId();
      const container = document.getElementById(id);

      if (!container) return;

      const iframe = container.querySelector('iframe');

      if (!iframe) return;

      loadedSlotIds.add(id);
      iframe.classList.add('rendered');
      window.setTimeout(() => container.classList.add('loaded'), 500);
    });

    slotOnloadListenerRegistered = true;
  };

  const setLanguageTargeting = (pubads) => {
    let lang = app?.i18n?.locale?.toLowerCase();

    if (!lang) {
      const seg = location.pathname.split('/').filter(Boolean)[0];
      lang = ['nl', 'fr', 'en', 'de', 'es'].includes(seg)
        ? seg
        : document.documentElement.lang;
    }

    if (lang) {
      pubads.setTargeting('lang', lang);
    }
  };

  const initAds = (retries = 0) => {
    if (retries >= MAX_RETRIES) return;

    if (!window.googletag || !window.googletag.apiReady) {
      return window.setTimeout(
        () => initAds(retries + 1),
        RETRY_DELAY_MS
      );
    }

    window.googletag.cmd.push(() => {
      const { googletag } = window;
      const containers = getSlotContainers();

      if (!containers.length) {
        return window.setTimeout(
          () => initAds(retries + 1),
          RETRY_DELAY_MS
        );
      }

      const pubads = googletag.pubads();

      registerSlotOnloadListener(pubads);

      if (!servicesEnabled) {
        setLanguageTargeting(pubads);
        pubads.enableSingleRequest();
        googletag.enableServices();
        servicesEnabled = true;
      }

      const newContainers = containers.filter(
        (container) => !definedSlots.has(container.id)
      );

      if (!newContainers.length) return;

      newContainers.forEach((container) => {
        try {
          const slot = googletag.defineSlot(
            `/${container.dataset.adUnitCode}`,
            ['fluid'],
            container.id
          );

          if (!slot) return;

          slot.addService(pubads);
          definedSlots.set(container.id, slot);
          googletag.display(container.id);
        } catch (error) {
          // Ignore individual slot failures and continue with the remaining slots.
        }
      });

      if (newContainers.length) {
        window.setTimeout(() => {
          if (!window.googletag || !window.googletag.apiReady) return;

          const staleSlots = newContainers
            .map((container) => definedSlots.get(container.id))
            .filter(
              (slot) => slot && !loadedSlotIds.has(slot.getSlotElementId())
            );

          if (!staleSlots.length) return;
          window.googletag.pubads().refresh(staleSlots);
        }, RETRY_DELAY_MS);
      }
    });
  };

  const startAds = () => {
    if (hasStarted) return;

    hasStarted = true;

    loadGptScript()
      .then(() => Vue.nextTick(() => initAds()))
      .catch(() => {});
  };

  const removeConsentListeners = () => {
    if (!handleConsentEvent) return;

    window.removeEventListener(CONSENT_STUDIO_UPDATED_EVENT, handleConsentEvent);
  };

  const waitForMarketingConsent = (
    eventType,
    retriesLeft = CONSENT_RECHECK_RETRIES
  ) => {
    if (hasMarketingConsentGranted()) {
      removeConsentListeners();
      startAds();
      return;
    }

    if (retriesLeft <= 0) return;

    window.setTimeout(() => {
      waitForMarketingConsent(eventType, retriesLeft - 1);
    }, CONSENT_RECHECK_DELAY_MS);
  };

  handleConsentEvent = (event) => waitForMarketingConsent(event.type);

  window.onNuxtReady(() => {
    if (hasMarketingConsentGranted()) {
      startAds();
      return;
    }

    window.addEventListener(CONSENT_STUDIO_UPDATED_EVENT, handleConsentEvent);
  });
};
